/**
 * Engraving: font swatches, skip (customization modal), char count, live preview.
 * Relies on data-variant-id / data-addon-price on input.product-engraving for add-on pricing.
 */
(function () {
  function cssFontStackFromField(str) {
    if (!str || !String(str).trim()) return 'serif';
    const s = String(str).trim();
    if (s.includes(',')) return s;
    if (s.includes("'") || s.includes('"')) return s;
    return `"${s}", serif`;
  }

  function formatPriceSuffix(cents, formatMoney) {
    const c = parseInt(cents, 10);
    if (Number.isNaN(c) || c <= 0) return '';
    if (typeof formatMoney === 'function') {
      let m = formatMoney(c).trim();
      if (m.endsWith('.00')) m = m.slice(0, -3);
      if (m.endsWith(',00')) m = m.slice(0, -3);
      return m.replace(/^\$/, '+$');
    }
    const d = c / 100;
    return d % 1 === 0 ? `+$${d}` : `+$${d.toFixed(2)}`;
  }

  function getAddOnsRoot(el) {
    return (el && el.closest && el.closest('product-add-ons')) || document.querySelector('product-add-ons');
  }

  function triggerPriceSync(root) {
    if (root && typeof root.updatePrice === 'function') root.updatePrice();
    if (root && typeof root.updateButtonState === 'function') root.updateButtonState();
  }

  function getEngravingSkipCheckbox(detailsEl) {
    if (!detailsEl) return null;
    const inside = detailsEl.querySelector('.engraving-skip');
    if (inside) return inside;
    const section = detailsEl.closest('.customization-option-section--engraving');
    return section ? section.querySelector('.engraving-skip') : null;
  }

  function initEngravingRoot(detailsEl) {
    const textInput = detailsEl.querySelector('input.product-engraving');
    if (!textInput) return;

    const root = getAddOnsRoot(detailsEl);
    const formatMoney = root && typeof root.formatMoney === 'function' ? root.formatMoney.bind(root) : null;
    const skipBox = getEngravingSkipCheckbox(detailsEl);
    const panel = detailsEl.querySelector('[data-engraving-panel]');
    const swatches = detailsEl.querySelectorAll('[data-engraving-font-swatch]');
    const fontHidden = detailsEl.querySelector('input.product-engraving-font');
    const previewText = detailsEl.querySelector('[data-engraving-preview-text]');
    const pickLabel = detailsEl.querySelector('[data-choose-font-pick]');
    const charLeft = detailsEl.querySelector('[data-engraving-chars-left]');
    const hasSwatches = swatches.length > 0;

    function getSelectedSwatch() {
      return detailsEl.querySelector('.engraving-font-swatch.is-selected');
    }

    function setSwatchUI(button) {
      swatches.forEach((b) => {
        const on = b === button;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        const fam = b.getAttribute('data-font-family') || '';
        const inner = b.querySelector('.engraving-font-swatch-aa');
        if (inner) inner.style.fontFamily = cssFontStackFromField(fam);
      });
    }

    function applyFontFromSwatch(button) {
      if (!button || !textInput) return;
      setSwatchUI(button);
      const baseCents = parseInt(textInput.getAttribute('data-engraving-base-cents') || '0', 10) || 0;
      const fontCents = parseInt(button.getAttribute('data-font-cents') || '0', 10) || 0;
      const totalCents = baseCents + fontCents;
      const baseVid = textInput.getAttribute('data-engraving-base-variant-id');
      const fontVid = button.getAttribute('data-font-variant-id');

      textInput.setAttribute('data-addon-price', String(totalCents));
      if (baseVid) {
        textInput.setAttribute('data-variant-id', baseVid);
      } else if (fontVid) {
        textInput.setAttribute('data-variant-id', fontVid);
      } else {
        textInput.removeAttribute('data-variant-id');
      }
      if (fontVid) {
        textInput.setAttribute('data-engraving-font-variant-id', fontVid);
      } else {
        textInput.removeAttribute('data-engraving-font-variant-id');
      }
      const fl = button.getAttribute('data-font-label') || '';
      if (fontHidden) {
        fontHidden.value = fl;
      }
      if (pickLabel) {
        const fontPrefix = formatPriceSuffix(fontCents, formatMoney);
        pickLabel.textContent = fl ? ` ${fl}${fontPrefix ? ` (${fontPrefix})` : ''}` : '';
      }
      if (previewText) {
        previewText.style.fontFamily = cssFontStackFromField(button.getAttribute('data-font-family') || fl);
      }
      textInput.dispatchEvent(new Event('input', { bubbles: true }));
      textInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function updateCharCount() {
      if (!charLeft) return;
      const limit = parseInt(
        textInput.getAttribute('data-character-limit') || textInput.getAttribute('maxlength') || '0',
        10
      );
      if (!limit || limit < 1) {
        charLeft.textContent = '—';
        return;
      }
      const used = (textInput.value || '').length;
      charLeft.textContent = String(Math.max(0, limit - used));
    }

    function updatePreview() {
      if (!previewText) return;
      if (skipBox && skipBox.checked) {
        previewText.textContent = '';
        return;
      }
      previewText.textContent = (textInput.value || '').trim();
    }

    function setSkipped(skipped) {
      detailsEl.toggleAttribute('data-engraving-skip-active', Boolean(skipped));
      if (panel) {
        panel.classList.toggle('engraving-panel--skipped', Boolean(skipped));
        if (skipped) {
          panel.setAttribute('aria-hidden', 'true');
        } else {
          panel.removeAttribute('aria-hidden');
        }
      }
      if (skipped) {
        textInput.value = '';
        textInput.removeAttribute('data-variant-id');
        textInput.setAttribute('data-addon-price', '0');
        if (fontHidden) fontHidden.value = '';
        if (skipBox) skipBox.setAttribute('data-was-skipped', '1');
      } else {
        if (hasSwatches) {
          const sel = getSelectedSwatch() || swatches[0];
          if (sel) applyFontFromSwatch(sel);
        }
        if (skipBox) skipBox.removeAttribute('data-was-skipped');
      }
      updateCharCount();
      updatePreview();
      textInput.dispatchEvent(new Event('input', { bubbles: true }));
      textInput.dispatchEvent(new Event('change', { bubbles: true }));
      triggerPriceSync(root);
    }

    if (hasSwatches) {
      const first = detailsEl.querySelector('.engraving-font-swatch.is-selected') || swatches[0];
      if (first) applyFontFromSwatch(first);

      swatches.forEach((btn) => {
        const inner = btn.querySelector('.engraving-font-swatch-aa');
        if (inner) {
          inner.style.fontFamily = cssFontStackFromField(btn.getAttribute('data-font-family') || '');
        }
        btn.addEventListener('click', function () {
          if (skipBox && skipBox.checked) return;
          applyFontFromSwatch(btn);
        });
      });
    } else if (pickLabel) {
      pickLabel.textContent = '';
    }

    textInput.addEventListener('input', function () {
      if (root && typeof root.clampEngravingValue === 'function') {
        root.clampEngravingValue(textInput);
      }
      updateCharCount();
      updatePreview();
    });

    if (skipBox) {
      skipBox.addEventListener('change', function () {
        setSkipped(skipBox.checked);
      });
    }

    updateCharCount();
    updatePreview();
  }

  function initAll() {
    document.querySelectorAll('details[data-engraving-root]').forEach((el) => initEngravingRoot(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
